// ============================================================
// ota-ready.ts — tell Capgo the freshly-applied bundle booted
// ============================================================
// This module is imported FIRST by src/main.tsx, before App.tsx, before the
// CSS, before anything else. That ordering is the whole point, so don't
// reorder that import.
//
// Why: @capgo/capacitor-updater rolls a newly-applied OTA bundle back unless
// notifyAppReady() lands within appReadyTimeout (10s, capacitor.config.ts).
// That safety net exists so a white-screening bundle self-heals. But the call
// used to sit ~50 lines into main.tsx — AFTER validateEnvironment() (whose own
// comment says it "will throw if invalid"), initErrorMonitor(),
// installChunkReloadGuard(), two StatusBar calls, and the entire App.tsx +
// stylesheet import chain. Any throw anywhere in that prefix aborts module
// evaluation, notifyAppReady() never fires, and a perfectly good bundle gets
// rolled back.
//
// Observed on Celia's iPhone (native build 521) 2026-08-23: it applied bundle
// 1.0.259 at 02:18 and reported `up-to-date`, was back on the builtin 1.0.3 by
// 02:31, and was then re-offered the same bundle on all 12 following check-ins
// — download, apply, roll back, forever, never moving off month-old JS. The
// ota_checkins table (#1182) is what made that visible; before it the device
// just looked "stuck".
//
// What this costs: the net no longer catches "JS evaluated but the app crashed
// later" — that bundle now gets marked good. That trade is deliberate. The
// scenario worth auto-healing is a bundle that cannot run at all (corrupt or
// truncated JS, a missing chunk), and that still fails to reach this line, so
// it still rolls back. A bundle that boots far enough to execute an import but
// then fails is better surfaced by the error monitor than papered over by a
// silent revert to month-old code the user never asked for.
//
// Errors are reported rather than swallowed. The previous call site ended in a
// bare `.catch(() => {})`, so a rejecting notifyAppReady() — the exact thing
// that would cause a rollback — left no trace anywhere.

import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

export interface OtaReadyDeps {
  isNative: () => boolean;
  notifyAppReady: () => Promise<unknown>;
  /** Called with whatever went wrong. Never throws to the caller. */
  onError?: (error: unknown) => void;
}

/**
 * Signal "this bundle booted" to the updater.
 *
 * Returns true when the signal was dispatched (native only), false on web or
 * when the plugin threw synchronously. Never throws: a failure here must not
 * take down app startup, which is the thing it exists to protect.
 */
export function signalOtaBundleReady(deps: OtaReadyDeps): boolean {
  // Everything is inside the try, the platform probe included: this runs as the
  // first statement of app startup, so a throw here would take down the boot it
  // exists to protect.
  try {
    if (!deps.isNative()) return false;
    void Promise.resolve(deps.notifyAppReady()).catch((error) => {
      deps.onError?.(error);
    });
    return true;
  } catch (error) {
    // Some plugin builds throw synchronously when the native bridge is absent.
    deps.onError?.(error);
    return false;
  }
}

/** Production wiring. Called on import — see the header. */
export function signalOtaBundleReadyNow(): boolean {
  return signalOtaBundleReady({
    isNative: () => {
      try {
        return Capacitor.isNativePlatform();
      } catch {
        return false;
      }
    },
    notifyAppReady: () => CapacitorUpdater.notifyAppReady(),
    onError: (error) => {
      // Console only: the error monitor initialises later in main.tsx, and the
      // whole point of this module is to run before anything that can fail.
      console.error('[OTA_READY] notifyAppReady failed — bundle may roll back', error);
    },
  });
}

signalOtaBundleReadyNow();
